import { Link } from "react-router-dom";
import { Check, Award, Globe2, BookOpen, Sparkles, Flame, ArrowRight } from "lucide-react";

const PROFILE = "https://customer-assets.emergentagent.com/job_dr-tannu-vedic/artifacts/8ljipjvs_IMG_3520.png";
const BOOK_COVER = "https://customer-assets.emergentagent.com/job_dr-tannu-vedic/artifacts/h1hp4nil_0e90368f-4448-46e1-9584-5bc3a35607eb.JPG";

const SPECIALIZATIONS = [
    "Vedic Astrology",
    "Financial Astrology",
    "Karma Guide Astrology",
    "Bhrigu Nandi Nadi (BNN)",
    "Numerology (Chaldean)",
    "Career Astrology",
    "Marriage Matching",
    "Business Astrology",
    "Foreign Settlement",
    "Health Predictions",
    "Annual Forecasting",
    "Muhurat & Remedies",
    "Relationship Counselling",
];

export default function About() {
    return (
        <div data-testid="about-page" className="pt-32 pb-20">
            <div className="container-vv">
                {/* Header */}
                <div className="max-w-3xl">
                    <div className="label-eyebrow">About</div>
                    <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-cream mt-4 text-balance">
                        Meet <span className="italic text-gold">Dr. Tannu Aggarwal</span>
                    </h1>
                    <p className="mt-6 text-cream-muted leading-relaxed text-lg">
                        A modern voice carrying an ancient torch. Dr. Tannu blends rigorous classical Jyotish with a warm, pragmatic counselling style — helping seekers worldwide transform confusion into clarity.
                    </p>

                    <div className="mt-8 flex flex-wrap gap-2">
                        <span className="inline-flex items-center gap-2 text-xs tracking-widest uppercase border border-gold/40 text-gold px-3 py-1.5 rounded-full"><Sparkles className="h-3.5 w-3.5" /> Vedic Astrologer</span>
                        <span className="inline-flex items-center gap-2 text-xs tracking-widest uppercase border border-gold/40 text-gold px-3 py-1.5 rounded-full">🏅 PhD Gold Medalist</span>
                        <span className="inline-flex items-center gap-2 text-xs tracking-widest uppercase border border-gold/40 text-gold px-3 py-1.5 rounded-full">🏅 MA Vastu Gold Medalist</span>
                        <span className="inline-flex items-center gap-2 text-xs tracking-widest uppercase border border-gold/40 text-gold px-3 py-1.5 rounded-full"><Flame className="h-3.5 w-3.5" /> Karma Guide</span>
                        <span className="inline-flex items-center gap-2 text-xs tracking-widest uppercase border border-gold/40 text-gold px-3 py-1.5 rounded-full">BNN Expert</span>
                        <span className="inline-flex items-center gap-2 text-xs tracking-widest uppercase border border-gold/40 text-gold px-3 py-1.5 rounded-full"><BookOpen className="h-3.5 w-3.5" /> Published Author</span>
                    </div>
                </div>

                {/* Profile bento */}
                <div className="mt-16 grid lg:grid-cols-12 gap-8">
                    <div className="lg:col-span-5">
                        <div className="relative">
                            <div className="absolute -inset-4 bg-gold/10 blur-3xl rounded-full" />
                            <div className="relative aspect-[3/4] rounded-2xl overflow-hidden border border-gold/30 glow-gold">
                                <img src={PROFILE} alt="Dr. Tannu Aggarwal" className="w-full h-full object-cover" />
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-7 space-y-8">
                        <div className="card-luxe">
                            <div className="flex items-center gap-3 mb-4">
                                <Award className="h-5 w-5 text-gold" />
                                <h3 className="font-serif text-xl text-cream">A Lineage of Learning</h3>
                            </div>
                            <p className="text-cream-muted leading-relaxed text-sm">
                                Dr. Tannu Aggarwal is a <strong className="text-cream">Gold Medalist in PhD</strong> and <strong className="text-cream">Gold Medalist in MA (Vastu)</strong> — an academic record that anchors her practice in rigorous scholarship. Trained for over 9 years under senior Jyotish acharyas, her deep specialization in Bhrigu Nandi Nadi — one of the rarest predictive systems — sets her apart. She has interpreted over 8,000 birth charts and continues daily research into classical texts.
                            </p>
                        </div>

                        <div className="card-luxe">
                            <div className="flex items-center gap-3 mb-4">
                                <Globe2 className="h-5 w-5 text-gold" />
                                <h3 className="font-serif text-xl text-cream">Global Practice</h3>
                            </div>
                            <p className="text-cream-muted leading-relaxed text-sm">
                                Clients from 25+ countries — India, USA, UK, Canada, UAE, Singapore, Australia — meet Dr. Tannu virtually. Her bilingual fluency (English/Hindi) and warm, non-judgmental sessions create instant trust.
                            </p>
                        </div>

                        <div className="card-luxe">
                            <div className="flex items-center gap-3 mb-4">
                                <BookOpen className="h-5 w-5 text-gold" />
                                <h3 className="font-serif text-xl text-cream">Philosophy</h3>
                            </div>
                            <p className="text-cream-muted leading-relaxed text-sm">
                                "Astrology is a map — not a verdict. My role is to read the terrain honestly so you can walk it consciously." Dr. Tannu refuses fear-based remedies and overpriced gemstones; she recommends only what is ethical, chart-specific, and effective.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Specializations */}
                <div className="mt-20">
                    <div className="flex items-center gap-3 mb-6">
                        <Sparkles className="h-5 w-5 text-gold" />
                        <h2 className="font-serif text-3xl text-cream">Areas of Expertise</h2>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {SPECIALIZATIONS.map((s) => (
                            <div key={s} className="flex items-center gap-3 bg-ink-surface/40 border border-gold/15 rounded-xl px-5 py-4">
                                <Check className="h-4 w-4 text-gold flex-shrink-0" />
                                <span className="text-cream text-sm">{s}</span>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="mt-16 text-center">
                    <Link to="/booking" className="btn-gold" data-testid="about-cta-book">Book a Consultation</Link>
                </div>

                {/* Author / Book section */}
                <div className="mt-24" data-testid="about-book-section">
                    <div className="grid lg:grid-cols-12 gap-12 items-center">
                        <div className="lg:col-span-5 order-2 lg:order-1">
                            <div className="label-eyebrow flex items-center gap-2"><BookOpen className="h-3.5 w-3.5" /> As a Published Author</div>
                            <h2 className="font-serif text-3xl sm:text-4xl text-cream mt-4 text-balance">
                                <span className="italic text-gold">Karakatva</span> in Vedic Astrology
                            </h2>
                            <p className="mt-4 text-cream-muted leading-relaxed">
                                Dr. Tannu&apos;s first book — <em>The Complete Guide to Karakatva in Vedic Astrology</em> — is a practitioner&apos;s reference for the doctrine of significations. Distilled from years of consultation work and classical study, it gives both students and seasoned astrologers a clear, working methodology.
                            </p>
                            <div className="mt-6 flex flex-wrap gap-3 text-sm text-cream-muted">
                                <div className="border border-gold/20 rounded-lg px-4 py-2 bg-ink-deep/40">Paperback · <span className="text-gold">₹950</span></div>
                                <div className="border border-gold/20 rounded-lg px-4 py-2 bg-ink-deep/40">Hardcover · <span className="text-gold">₹1,150</span></div>
                            </div>
                            <div className="mt-8">
                                <Link to="/book" className="btn-gold" data-testid="about-book-cta">
                                    Buy the Book <ArrowRight className="h-4 w-4" />
                                </Link>
                            </div>
                        </div>
                        <div className="lg:col-span-7 order-1 lg:order-2">
                            <div className="relative max-w-md mx-auto">
                                <div className="absolute -inset-6 bg-gold/10 blur-3xl rounded-full" />
                                <div className="relative aspect-[4/5] rounded-2xl overflow-hidden border border-gold/30 bg-cream">
                                    <img src={BOOK_COVER} alt="The Complete Guide to Karakatva in Vedic Astrology" className="w-full h-full object-contain" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
